import React, { useEffect } from 'react';

import Alitalk from '@alife/icbu-alitalk-react';

import CartStore from '@/stores/cart';
import DrawerStore from '@/stores/drawer';
import ExpressStore from '@/stores/express';
import NoticeStore from '@/stores/notice';
import PageStore from '@/stores/page';
import SkuStore from '@/stores/sku';

import { domainHost } from '@/common/country-site';
import DetailIcon from '@/common/detail-icon';
import { isIframe, postPo } from '@/common/iframe-post';
import doLog from '@/common/log';
import xman from '@/common/xman';


/***
 * order表单会处理相关逻辑 @common/payment
 * chat、inquiry 无需处理登录，需要主动关闭弹窗
 * PO/Cart 需要处理登录及moq提示
 *
 * @param target 'order|chat|inquiry|cart|po|message'
 * @param action 跳转地址
 * @param style 'icon|primary|normal|iconWithText'
 * @param text 美杜莎文本
 * @param log 打点key
 * @param type 'product|sample'
 * @param rule 'pageSku|productSku|sampleSku'
 */

export default function ActionButton(
  props: DevTypes.ButtonClass & {
    i18n: DetailTypes.i18n;
    rule: DevTypes.SkuTypeInfo;
    productOwnMemberAlitalkEncryptId: string;
    productId: number | string;
    productEncryptId: string; //加密后的productId
    umidToken: string; //xman挂到页面的参数，用来表示用户是否登陆
    price: DetailTypes.Price;
    renderConfig?: any;
    target: DevTypes.ButtonTypeInfo;
    moq: number;
    type: SkuTypes.TypeInfo;
    atmSecurityToken: string;
  }
) {
  const {
    text,
    i18n,
    target,
    log,
    type,
    action,
    productId,
    productOwnMemberAlitalkEncryptId,
    price,
    rule,
    productEncryptId,
    umidToken,
    moq,
    style = '',
    renderConfig,
    atmSecurityToken = '',
  } = props;

  const { dialogShow, dialogFrom, dialogType } = DrawerStore(state => state);
  const secondScreen = PageStore(state => state.secondScreen);
  const skuInfo = SkuStore(state => state.product);
  const cartStore = CartStore(state => state);
  const noticeStore = NoticeStore(state => state);
  const expressStore = ExpressStore(state => state);

  const { quantity, isSoldOut: skuSoldOut } = skuInfo;
  const params =
    quantity > 0
      ? Object.values(skuInfo.mapInfo)
          .filter(i => i.quantity > 0)
          .map(i => ({
            productId,
            skuId: i.skuId,
            quantity: i.quantity,
          }))
      : [{ productId }];

  // 加购错误提示
  useEffect(() => {
    if (target === 'cart' && cartStore.data.success) {
      // 错误信息
      if (cartStore.data.message) {
        noticeStore.modify(`${cartStore.data.message}_error`);
      }
      // 成功提示
      else {
        noticeStore.modify(`${i18n['detail_pc_add_to_cart_sucess']}_success`);
      }
      cartStore.del();
    }
  }, [cartStore.data.success]);

  // 弹窗来源逻辑,只展示一个,排除sku面板
  if (renderConfig?.isSkuPanel) {
    if (dialogFrom && dialogFrom !== target && target !== 'chat' && target !== 'message') {
      return null;
    }
  } else {
    if (rule !== 'pageSku' && target !== 'message' && dialogFrom && dialogFrom !== target) {
      return null;
    }
  }

  const handleClick = () => {
    doLog(log, `second=${secondScreen}`);
    // DropShipping
    if (target === 'drop') {
      import('@ali/dser-import-product-dialog').then(math =>
        math.default({
          productId,
        })
      );
      return;
    }
    // 沟通、询盘关闭弹窗，不处理
    if (target === 'chat' || target === 'message' || target === 'inquiry') {
      if (dialogType) {
        dialogShow({
          type,
        });
      }
      return;
    }
    // 弹窗
    if (rule === 'pageSku') {
      dialogShow({
        type,
        from: target,
      });
    }
    // productSku
    else {
      if (skuSoldOut) return noticeStore.modify(i18n['detail_sku_soldout_tips']);
      // Complete order request || Add to cart
      if (target === 'po' || target === 'cart') {
        // 加购未选数量提示
        if (quantity === 0) return noticeStore.modify(i18n['detail_sku_quantityneeded_tips']);
        // po moq提示
        if (target === 'po' && quantity < moq)
          return noticeStore.modify(
            i18n['detail_sku_moqneeded_tips'].replace(
              '{0}',
              `${moq} ${moq > 1 ? price.unitEven : price.unit}`
            )
          );
        // 登录拦截，其它自带无需处理
        xman(() => {
          // 意向单
          if (target === 'po') {
            const url = action?.replace('{0}', decodeURIComponent(JSON.stringify(params))) || '';
            // quick view
            if (isIframe) {
              postPo(url);
              return;
            }
            window.location.href = url;
            return;
          }
          // 购物车
          cartStore.add(
            Object.values(skuInfo.mapInfo)
              .filter(i => i.quantity > 0)
              .map(i => ({
                productId: `${productId}`,
                quantity: i.quantity,
                dispatchLocation: expressStore.dispatchId,
                carrierCode: expressStore.product.express?.methodCode,
                skuId: i.skuId,
              }))
          );
        });
      }

      // 下单自行提交，不处理
      if (target === 'order') {
        // 用增特殊诉求，https://space.o2.alibaba-inc.com/issue/52933546
        window.postMessage({ eventName: 'start_order_click' }, '*');
        return;
      }
    }
  };

  // 面板无物流disabled
  const isSoldOut = () => {
    if ((target === 'order' || target === 'cart') && expressStore[type].isEmpty && quantity > moq) {
      return true;
    }
    return false;
  };

  const renderButton = () => {
    if (style === 'icon' || style === 'iconWithText') {
      if (target === 'cart') {
        return <DetailIcon symbol="icon-cart-add" className={'detail-symbol'} />;
      }
      if (target === 'chat') {
        return <DetailIcon className="detail-symbol" symbol={'icon-message-comments'} />;
      }
      if (target === 'inquiry') {
        return <DetailIcon className="detail-symbol" symbol={'icon-email'} />;
      }
    }
    return i18n[text];
  };

  // 沟通、询盘
  if (target === 'chat' || target === 'message' || target === 'inquiry') {
    // 排除Quick View
    if (rule !== 'quickSku') {
      const scene = {
        fromPage: 'product_detail',
        bizType: target === 'inquiry' ? 'contact_supplier' : 'chat_now',
        bucket: '',
      };
      const inquiryUrl = `//message.${domainHost()}/msgsend/contact.htm?action=contact_action&domain=1&id=${productId}&id_f=${productEncryptId}&mloca=main_en_detail&tracelog=tracedetailfeedback&umidToken=${umidToken}&chatToken=${atmSecurityToken}`;
      return (
        <Alitalk
          uid={productOwnMemberAlitalkEncryptId}
          pid={productId}
          bizType="1"
          bizId={productId}
          action={target === 'inquiry' ? 'chatForInquiry' : 'chatNow'}
          className={`button-item ${style}`}
          inquiryUrl={inquiryUrl}
          sceneType="detail"
          scene={scene} // 场景来源参数
          onClick={handleClick}
          chatToken={atmSecurityToken}
        >
          <button
            data-type-btn={target}
          >
            {renderButton()}
          </button>
          {style === 'iconWithText' && <div className='icon_desc'>{i18n[text]}</div>}
        </Alitalk>
      );
    }
  }

  return (
    <button
      data-type-btn={target}
      disabled={isSoldOut()}
      className={`button-item ${style}`}
      type={target === 'order' && rule !== 'pageSku' ? 'submit' : 'button'} // 下单且非pageSku，触发 form submit action
      onClick={handleClick}
    >
      {renderButton()}
    </button>
  );
}
